import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@material-ui/core/Button/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import LaunchIcon from '@material-ui/icons/Launch';
import {
  ApiFilterOperation,
  Params,
  PromptContext,
  PromptDialogContext,
  PromptProvider,
  ResultDto,
  SnackbarContext,
  SnackbarVariant,
  TableField,
  useFetch,
  usePrompts,
} from '@eas/common-web';
import {
  TableFieldHandle,
  TableFieldProps,
} from '@eas/common-web/dist/types/components/table-field/table-field-types';
import { canMuzaResync } from '@modules/work-order/work-order-actionbar';
import { useWorkOrderStateColumn } from '@modules/work-order/work-order-utils';
import { WorkOrder } from '@models';
import { EvidenceApiUrl } from '@enums';
import { CalendarContext } from '../../calendar-context';
import { ResyncBulkFormButton } from './bulk-operation-resync';
import { workOrderBulkApi } from './bulk-operations-api';
import { BulkChangeStateButton } from './bulk-operations-change-state-menu';
import {
  BulkOperationDialogContextType,
  BulkOperationsContext,
  BulkSelectedTask,
} from './bulk-operations-context';
import { ExpectedMaterialBulkFormButton } from './bulk-operations-expected-material-menu';

export const BULK_OPERATION_FORM_KEY_CALENDAR = `BULK_OPERATION_FORM_KEY_CALENDAR`;
export const BULK_OPERATION_FORM_KEY_TABLE = `BULK_OPERATION_FORM_KEY_TABLE`;

export function useBulkOperationsForm() {
  const bulkContext = useContext(BulkOperationsContext);

  const { dataSource, workOrderTableRef } = useContext(CalendarContext);

  usePrompts(
    [
      {
        key: BULK_OPERATION_FORM_KEY_CALENDAR,
        dialogTitle: `Aplikovať hromadnú akciu`,
        dialogText: (
          <BulkOperationsCalendarDialog
            selected={bulkContext.selected}
            refresh={() => {
              dataSource?.yAxis?.refreshData?.();
              workOrderTableRef?.current?.refresh();
            }}
            bulkContext={bulkContext}
          />
        ),
        dialogWidth: 1200,
        dialogShowConfirm: false,
      },
    ],
    [bulkContext, dataSource?.yAxis?.refreshData]
  );
}

const createWoIdsList = (selected: BulkSelectedTask[]) => {
  return [
    ...selected.filter((s) => !s.isMultiTask).map((s) => s.id),
    ...selected.filter((s) => s.isMultiTask).flatMap((s) => s.multitaskWoIds!),
  ];
};

const useWorkOrderList = (ids: string[]) => {
  const params: Params = {
    filters: [
      {
        field: 'id',
        operation: ApiFilterOperation.IN,
        values: ids,
      },
    ],
    size: -1,
  };
  const [result, loading] = useFetch<ResultDto<WorkOrder>>(
    `${EvidenceApiUrl.WORK_ORDERS}/list`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify(params),
    }
  );
  return { result, loading };
};

const useColumns = (): TableFieldProps<WorkOrder>['columns'] => [
  { datakey: 'workOrderId', name: 'Číslo zákazky', width: 230 },
  {
    datakey: 'locked',
    name: 'Uzamknutá',
    width: 80,
    CellComponent: ({ value }) => {
      return (
        <div
          style={{
            display: 'flex',
            gap: 16,
            margin: '4px 0',
            alignItems: 'center',
          }}
        >
          {value ? 'Áno' : 'Nie'}
        </div>
      );
    },
  },
  {
    datakey: 'currentState',
    name: 'Stav',
    width: 150,
    CellComponent: function ({ value }) {
      return useWorkOrderStateColumn(value);
    },
  },
  {
    datakey: 'expectedMaterial',
    name: 'Očakávaný materiál',
    width: 120,
    CellComponent: ({ value }) => {
      return (
        <div
          style={{
            display: 'flex',
            gap: 16,
            margin: '4px 0',
            alignItems: 'center',
          }}
        >
          {value ? 'Áno' : 'Nie'}
        </div>
      );
    },
  },

  {
    datakey: 'id',
    name: 'Detail',
    width: 80,
    CellComponent: ({ value }) => (
      <Button
        onClick={() => window.open(`/zsd/pracovne-prikazy/${value}`, '_blank')}
      >
        <LaunchIcon fontSize="small" />
      </Button>
    ),
  },
];

function hasState(codes: string[], currentState: string | undefined) {
  return codes.includes(currentState || '');
}

const BulkOperationsCalendarDialog = ({
  selected,
  refresh,
  bulkContext,
}: {
  selected: BulkSelectedTask[];
  refresh: () => void;
  bulkContext: BulkOperationDialogContextType;
}) => {
  const { loading, result } = useWorkOrderList(createWoIdsList(selected));
  const data = useMemo(() => result?.items ?? [], [result?.items]);
  const promptDialogContext = useContext(PromptDialogContext);
  return (
    <BulkOperationFormDialog
      afterSubmit={() => {
        bulkContext.clearSelection();
        bulkContext.setBulkEditModeOn(false);
        promptDialogContext?.handleCancel?.();
      }}
      data={data}
      loading={loading}
      refresh={refresh}
      totalCount={result?.count ?? 0}
    />
  );
};

export const BulkOperationFormWorkOrderTable = ({
  selected,
  refresh,
}: {
  selected: string[];
  refresh: () => void;
}) => {
  const { loading, result } = useWorkOrderList(selected);
  const data = useMemo(() => result?.items ?? [], [result?.items]);
  const promptDialogContext = useContext(PromptDialogContext);
  return (
    <BulkOperationFormDialog
      afterSubmit={() => {
        promptDialogContext?.handleCancel?.();
      }}
      data={data}
      loading={loading}
      refresh={refresh}
      totalCount={result?.count ?? 0}
    />
  );
};

const BulkOperationFormDialog = ({
  totalCount,
  data,
  loading,
  refresh,
  afterSubmit,
}: {
  totalCount: number;
  data: WorkOrder[];
  loading: boolean;
  refresh: () => void;
  afterSubmit: () => void;
}) => {
  const ref = useRef<TableFieldHandle>(null);
  const columns = useColumns();
  const [selectedCount, setSelectedCount] = useState<number>(data?.length ?? 0);
  const [bulkOpLoading, setBulkOpLoading] = useState(false);

  const { testPrompt } = useContext(PromptContext);
  const { showSnackbar } = useContext(SnackbarContext);

  useEffect(() => {
    ref?.current?.setSelectedIndexes(data.map((_, i) => i));
    setSelectedCount(data?.length ?? 0);
  }, [data]);

  return (
    <PromptProvider>
      <p>
        Zoznam vybraných pracovných príkazov ({selectedCount} / {totalCount}
        ):
      </p>
      {(loading || bulkOpLoading) && <LinearProgress />}
      <TableField
        columns={columns}
        onChange={() => {}}
        value={data}
        visibleEdit={false}
        visibleAdd={false}
        visibleRemove={false}
        showToolbar={true}
        ref={ref}
        showRadioCond={() => false}
        showCheckboxCond={() => true}
        ToolbarComponent={({ selectedIndexes }) => {
          setSelectedCount(selectedIndexes?.length ?? 0);
          const unplanDisabled = (selectedIndexes ?? []).some(
            (index) =>
              !hasState(
                ['NAP', 'ODL', 'ODM'],
                data?.[index]?.currentState?.code
              )
          );

          const isEmpty = !selectedIndexes || selectedIndexes.length === 0;
          const selectedData =
            (data ?? []).filter((_, i) => selectedIndexes?.includes?.(i)) ?? [];

          const resyncableWos = (selectedData ?? []).filter((w) =>
            canMuzaResync(w, false)
          );
          return (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                justifyContent: 'flex-start',
                margin: '8px 0',
              }}
            >
              <BulkChangeStateButton
                afterSubmit={() => refresh()}
                workOrders={selectedData}
                setLoading={setBulkOpLoading}
                disabled={loading || bulkOpLoading}
              />
              <Button
                onClick={async () => {
                  setBulkOpLoading(true);
                  testPrompt({
                    key: 'WORK_ORDER_BULK_UNPLAN_ALERT',
                    submitCallback: async () => {
                      await workOrderBulkApi.unplan(
                        selectedData.map((w) => w.id)
                      );
                      setBulkOpLoading(false);
                      afterSubmit();
                      refresh();
                    },
                  });
                }}
                variant="contained"
                color="primary"
                style={{ fontSize: '12px', padding: '4px 8px' }}
                disabled={loading || unplanDisabled || isEmpty || bulkOpLoading}
              >
                Odplánovať označené
              </Button>
              <Button
                style={{ fontSize: '12px', padding: '4px 8px' }}
                color="primary"
                variant="contained"
                onClick={async () => {
                  setBulkOpLoading(true);
                  await workOrderBulkApi.lock(selectedData.map((w) => w.id));
                  setBulkOpLoading(false);
                  afterSubmit();
                  refresh();
                }}
                disabled={loading || isEmpty || bulkOpLoading}
              >
                Zamknúť označené
              </Button>
              <Button
                style={{ fontSize: '12px', padding: '4px 8px' }}
                color="primary"
                variant="contained"
                onClick={async () => {
                  setBulkOpLoading(true);
                  await workOrderBulkApi.unlock(selectedData.map((w) => w.id));
                  setBulkOpLoading(false);
                  afterSubmit();
                  refresh();
                }}
                disabled={loading || isEmpty || bulkOpLoading}
              >
                Odomknúť označené
              </Button>

              <ExpectedMaterialBulkFormButton
                handleOnItemClick={async (expectedMaterial) => {
                  setBulkOpLoading(true);

                  try {
                    await workOrderBulkApi.expectedMaterial(
                      selectedData.map((w) => w.id),
                      expectedMaterial
                    );
                    setBulkOpLoading(false);
                    afterSubmit();
                    refresh();
                  } catch (error) {
                    showSnackbar(
                      'Operácia zlyhala: ' + _.get(error, 'code'),
                      SnackbarVariant.ERROR
                    );
                    setBulkOpLoading(false);
                  }
                }}
                disabled={loading || isEmpty || bulkOpLoading}
              />
              <ResyncBulkFormButton
                disabled={
                  loading ||
                  isEmpty ||
                  bulkOpLoading ||
                  resyncableWos.length === 0
                }
                ids={resyncableWos.map((w) => w.id)}
                showSnackbar={showSnackbar}
                afterSubmit={afterSubmit}
              />
            </div>
          );
        }}
      />
    </PromptProvider>
  );
};
